import { toast } from 'react-toastify';
import statusModal from '@pages/Forms/components/modals/statusModal';
import SubmissionModal from '@pages/Forms/components/modals/SubmissionModal';
import { useApp, useAquaticResources, useJpa, useModal, useRequest } from '@stores';
import {
  GeometryTypes,
  LoadingMessages,
  RequestStatus,
  RequestType,
  ResponseStatuses,
  StorageScopes,
} from '@src/utils/enums';

import * as turf from '@turf/turf';

import L from 'leaflet';
import { queryClient } from '@src/api/queryClient';
import { DASHBOARD_QUERY_KEYS } from '@src/api/services/dashboard';
import { PROJECT_QUERY_KEYS } from '@src/api/services/project';
import { AQUATIC_RESOURCES_QUERY_KEYS, AquaticResourcesQueries } from '@api/services/aquatic-resources/query';
import { router } from '@src/Router';

const handleSuccess = (signed, permitType, body, store, dispatch, submissionModal) => {
  const { setIsSavingProgress, setIsLoading } = useApp.getState();
  const { openModal } = useModal.getState();

  setIsSavingProgress(false);
  setIsLoading(false);
  if (signed) {
    toast.success(`${permitType} Request has been successfully submitted!`);
    openModal(submissionModal, {
      msg: body?.msg,
      status: body?.status ?? ResponseStatuses.Success,
    });
    router.navigate({ to: '/dashboard' });
  } else {
    toast.success(`${permitType} Request has been successfully saved!`);
    store.doUpdatePermitRequest(body?.data?.request?.permits?.[0]);
    dispatch({ type: 'UPDATE_REQUEST_API_DATA', payload: body?.data });
    dispatch({ type: 'UPDATE_REQUEST_FORM_DATA', payload: body?.data });
    dispatch({ type: 'UPDATE_REQUEST_OBJECT', payload: { requestStatus: RequestStatus.Draft } });
  }
};

const validateAquaticResourceGeometryBoundary = async () => {
  const geometryValidation = await AquaticResourcesQueries.validateAquaticResourceGeometryBoundary();
  const { setAquaticResourceGeometryBoundaryFailures, setAquaticResourceGeometryBoundaryInvalidPages } =
    useAquaticResources.getState();
  setAquaticResourceGeometryBoundaryFailures(geometryValidation?.data?.aquaticResourceIds ?? []);
  setAquaticResourceGeometryBoundaryInvalidPages(geometryValidation?.data?.invalidPages ?? []);
  return geometryValidation;
};

const removeHiddenJpaAnswers = ({ jpaRequestAnswerSets, jpaQuestionVisibility, jpaFiles, doDeleteFile }) =>
  jpaRequestAnswerSets.map((answerSet) => {
    return {
      ...answerSet,
      answers: answerSet.answers?.filter((answer) => {
        const questionID = answer.jpaQuestionID;
        const isHidden = jpaQuestionVisibility[questionID] === false;

        // Delete files for hidden questions
        if (isHidden && answer.hasJPAFileUploaded) {
          const questionFiles = jpaFiles.filter((file) => Number(file.jpaQuestionID) === questionID);
          questionFiles.forEach((file) => doDeleteFile(file.fileName, file.componentID));
        }

        return !isHidden;
      }),
      jpaDynamicTableAnswers: answerSet.jpaDynamicTableAnswers?.filter((dynamicAnswer) => {
        const questionID = dynamicAnswer.jpaQuestionID;
        const isHidden = jpaQuestionVisibility[questionID] === false;

        return !isHidden;
      }),
      jpaDynamicTableDeletedAnswers: answerSet.jpaDynamicTableAnswers?.reduce((acc, answer) => {
        const questionID = answer.jpaQuestionID;
        const isHidden = jpaQuestionVisibility[questionID] === false;

        if (isHidden) {
          return acc.concat(answer.jpaDynTableAnswerID);
        }
        return acc;
      }, answerSet.jpaDynamicTableDeletedAnswers),
      jpaContactAnswers: answerSet.jpaContactAnswers?.filter((contactAnswer) => {
        const questionID = contactAnswer.questionID;
        const isHidden = jpaQuestionVisibility[questionID] === false;

        return !isHidden;
      }),
    };
  });

const PermitsDataBundle = {
  name: 'PermitFormData',

  getReducer: () => {
    const initialState = {
      permits: [],
      gpPermitTypes: [],
      errors: {},
    };

    return (state = initialState, { type, payload }) => {
      switch (type) {
        case 'RESET_PERMIT':
          return initialState;
        case 'RESET_PERMIT_SECTION':
          return {
            ...state,
            permits: [
              {
                ...state.permits[0],
                [payload]: {},
              },
            ],
          };
        case 'UPDATE_PERMITS':
          return {
            ...state,
            permits: [{ ...state.permits[0], ...payload }],
          };
        case 'UPDATE_PERMITS_SUB':
          return {
            ...state,
            permits: [
              {
                ...state.permits[0],
                [payload.section]: {
                  ...state.permits[0][payload.section],
                  ...payload.data,
                },
              },
            ],
          };
        case 'CLEAR_PILE_DATA_BY_USE': {
          const { pileUse, dataTypesToClear } = payload;

          const updatedPermits = state.permits.map((permit) => {
            let newEsaConsultantInformation = { ...permit.esaConsultantInformation };

            // Only remove items that match the specified pileUse
            if (dataTypesToClear.includes('pileInstallMethods')) {
              newEsaConsultantInformation.pileInstallMethods = newEsaConsultantInformation?.pileInstallMethods?.filter(
                (method) => method.pileUse !== pileUse
              );
            }
            if (dataTypesToClear.includes('pileTypes')) {
              newEsaConsultantInformation.pileTypes = newEsaConsultantInformation?.pileTypes?.filter(
                (type) => type.pileUse !== pileUse
              );
            }
            if (dataTypesToClear.includes('pileDetails')) {
              newEsaConsultantInformation.pileDetails = newEsaConsultantInformation?.pileDetails?.filter(
                (detail) => detail.pileUse !== pileUse
              );
            }

            return {
              ...permit,
              esaConsultantInformation: newEsaConsultantInformation,
            };
          });

          return {
            ...state,
            permits: updatedPermits,
          };
        }
        case 'UPDATE_GP_PERMIT_TYPES':
          return {
            ...state,
            gpPermitTypes: payload,
          };
        case 'UPDATE_PERMITS_ERRORS':
          return { ...state, errors: payload };
        default:
          return state;
      }
    };
  },

  selectPermitData: (state) => state.PermitFormData.permits[0],
  selectPermitRequestID: (state) => state.PermitFormData.permits?.[0]?.permitID,
  selectGPPermitTypes: (state) => state.PermitFormData.gpPermitTypes,
  selectPermitErrors: (state) => state.PermitFormData.errors,

  doFetchPermit:
    (projectID, requestID, type, requestType, requestStatus, isGenerate = false, archived) =>
    ({ apiGet, dispatch, store }) => {
      const { setIsLoading } = useApp.getState();
      const { openSecondaryModal } = useModal.getState();
      const { resetToDefaults, setApiAnswers } = useJpa.getState();
      const { setProjectBounds, setProjectGeometry, setSelectedRequestId } = useRequest.getState();

      // Check if fetching an old request name
      if (requestType === RequestType.NationwidePermit) type = '9';
      else if (requestType === RequestType.IndividualPermit) type = '8';

      !isGenerate && setIsLoading(true, `Retrieving ${requestType} Request Information...`);

      store.doResetJDFormData();
      store.doResetRequestData();
      resetToDefaults();

      const uri =
        '/api/Permit/getPermit?' +
        new URLSearchParams({
          projectID,
          requestID,
        });

      return apiGet(uri, (err, body) => {
        !isGenerate && setIsLoading(false);
        if (!err && body.status === ResponseStatuses.Success) {
          const responseData = { ...body?.data, archived };
          const requestData = responseData?.request;
          const jpaBasicInfo = requestData?.jpaBasicInfo;
          const projectLocation = responseData?.location;
          const validPermitTypes = requestData?.jpaBasicInfo?.[0]?.validPermitTypes;
          const jpaRequestAnswerSets = requestData?.jpaRequestAnswerSets;
          const permitID = requestData?.permits?.[0]?.permitID;

          dispatch({ type: 'UPDATE_REQUEST_API_DATA', payload: responseData });
          dispatch({ type: 'UPDATE_REQUEST_FORM_DATA', payload: responseData });
          dispatch({
            type: 'UPDATE_REQUEST_OBJECT',
            payload: { requestStatus: requestStatus },
          });

          useJpa.setState({
            jpaBasicInfo,
            validPermitTypes,
            jpaRequestAnswerSets,
          });
          setApiAnswers();

          dispatch({
            type: 'UPDATE_PERMITS',
            payload: requestData?.permits?.filter((permit) => permit.permitID === permitID)[0],
          });
          if (isGenerate) {
            store.doGenerateDraftDocuments();
          } else {
            setProjectGeometry(projectLocation?.geometry);

            const geometryType = projectLocation?.geometry?.type;
            const validTypes = [
              GeometryTypes.LineString,
              GeometryTypes.MultiLineString,
              GeometryTypes.MultiPolygon,
              GeometryTypes.Point,
              GeometryTypes.Polygon,
            ];

            if (geometryType && validTypes.includes(geometryType)) {
              let geometry;

              if (geometryType === GeometryTypes.Point) {
                const point = turf.point(projectLocation.geometry.coordinates);
                geometry = turf.buffer(point, 5, { units: 'miles' });
              } else {
                geometry = projectLocation.geometry;
              }

              const bbox = turf.bbox(geometry);
              const bounds = L.latLngBounds([
                { lat: bbox[1], lng: bbox[0] },
                { lat: bbox[3], lng: bbox[2] },
              ]);
              setProjectBounds(bounds);
            }
            queryClient.invalidateQueries({
              queryKey: [
                PROJECT_QUERY_KEYS.GET_SHARED_CONTACTS,
                store.selectProjectID(),
                store.selectRequestID(),
                store.selectVersion(),
              ],
            });
            setSelectedRequestId(type);
            router.navigate({
              to: '/forms/$id',
              params: { id: String(permitID) },
            });
            store.doFetchUserFilesDetails({ componentID: permitID, scope: StorageScopes.ProjectFolder }).finally(() => {
              setIsLoading(false);
            });
          }
        } else {
          openSecondaryModal(statusModal, {
            msg: body?.msg ?? err?.msg ?? 'An error occurred while retrieving the request, please try again',
            status: body?.status ?? err?.status ?? 'Request Retrieval Error',
          });
          setIsLoading(false);
        }
      });
    },
  doAddPermit:
    (data) =>
    ({ apiPost, dispatch, store }) => {
      const { setIsLoading } = useApp.getState();
      const { openSecondaryModal, closeModal } = useModal.getState();
      const { resetToDefaults } = useJpa.getState();

      const permitType = RequestType.PermitApplication;

      setIsLoading(true, `Creating ${permitType} Request...`);
      resetToDefaults();

      const uri = '/api/Permit/AddPermit';

      return apiPost(uri, data, (err, body) => {
        setIsLoading(false);

        if (!err && body.status === ResponseStatuses.Success) {
          dispatch({
            type: 'UPDATE_REQUEST_API_DATA',
            payload: {
              projectID: body.data.projectID,
              projectVersion: body.data.projectVersion,
              request: { requestID: body.data.requestID, permission: body.data.permission },
            },
          });
          dispatch({
            type: 'UPDATE_REQUEST_FORM_DATA',
            payload: {
              projectID: body.data.projectID,
              projectVersion: body.data.projectVersion,
              request: { requestID: body.data.requestID },
            },
          });

          useJpa.setState({
            jpaBasicInfo: body.data.jpaBasicInfo,
            validPermitTypes: body.data.jpaBasicInfo?.[0]?.validPermitTypes,
          });

          dispatch({
            type: 'UPDATE_REQUEST_OBJECT',
            payload: { requestStatus: RequestStatus.Draft, requestID: body.data.requestID, requestType: permitType },
          });
          dispatch({
            type: 'UPDATE_PERMITS',
            payload: {
              permitID: body.data.permitID,
              requestID: body.data.requestID,
            },
          });
          router.navigate({ to: '/forms' });
          store.doFetchUserFilesDetails({ componentID: body.data.permitID, scope: StorageScopes.ProjectFolder });
          closeModal();

          queryClient.invalidateQueries({ queryKey: [DASHBOARD_QUERY_KEYS.GET_DASHBOARD] });
          queryClient.invalidateQueries({ queryKey: [PROJECT_QUERY_KEYS.GET_PROJECT_LOCATION] });
          queryClient.invalidateQueries({
            queryKey: [
              PROJECT_QUERY_KEYS.GET_SHARED_CONTACTS,
              store.selectProjectID(),
              store.selectRequestID(),
              store.selectVersion(),
            ],
          });
        } else {
          openSecondaryModal(statusModal, {
            msg: body?.msg ?? err?.msg ?? 'An error occurred while creating the request, please try again',
            status: body?.status ?? err?.status ?? 'Request Creation Error',
          });
        }
      });
    },
  doUpdatePermit:
    (data, options = {}) =>
    async ({ apiPut, dispatch, store }) => {
      const { setIsLoading, setIsSavingProgress } = useApp.getState();
      const { openSecondaryModal } = useModal.getState();
      const { jpaQuestionVisibility: jpaQuestionVisibilityObj, setApiAnswers, validPermitTypes } = useJpa.getState();

      const signed = Boolean(data?.request?.isSubmit);
      const requestId = data?.request?.requestID;
      const { hasArChanges = false } = options;
      setIsSavingProgress(true);
      setIsLoading(true, signed ? LoadingMessages.SubmitRequest : LoadingMessages.SaveRequest);
      const permitType = RequestType.PermitApplication;
      const jpaQuestionVisibility = jpaQuestionVisibilityObj;
      const uri = '/api/Permit/UpdatePermit';

      if (signed) {
        const geometryValidation = await validateAquaticResourceGeometryBoundary();
        if (geometryValidation?.status === ResponseStatuses.ValidationFailed) {
          setIsSavingProgress(false);
          setIsLoading(false);
          return;
        }
      }

      const requestedPermitType = data?.request?.permits?.[0]?.requestedPermitType;
      const jpaNotNeeded =
        Boolean(requestedPermitType) &&
        Array.isArray(validPermitTypes) &&
        !validPermitTypes.includes(requestedPermitType);

      if (data?.request?.jpaRequestAnswerSets && jpaNotNeeded) {
        data.request.jpaRequestAnswerSets = data.request.jpaRequestAnswerSets
          .filter((answerSet) => answerSet.jpaRequestAnswerSetID > 0)
          .map((answerSet) => {
            const deletedTableAnswerIds = [
              ...new Set([
                ...(answerSet.jpaDynamicTableDeletedAnswers ?? []),
                ...(answerSet.jpaDynamicTableAnswers ?? []).map((answer) => answer.jpaDynTableAnswerID),
              ]),
            ].filter((answerId) => answerId > 0);

            return {
              answers: [],
              jpaContactAnswers: [],
              jpaDynamicTableAnswers: [],
              jpaDynamicTableDeletedAnswers: deletedTableAnswerIds,
              jpaRequestAnswerSetID: answerSet.jpaRequestAnswerSetID,
              jpaFormID: answerSet.jpaFormID,
              requestID: answerSet.requestID,
              version: answerSet.version,
            };
          });
      } else if (data?.request?.jpaRequestAnswerSets && jpaQuestionVisibility) {
        const jpaFiles = store.selectJpaFiles();
        const cleanedAnswerSets = removeHiddenJpaAnswers({
          jpaRequestAnswerSets: data.request.jpaRequestAnswerSets,
          jpaQuestionVisibility,
          jpaFiles,
          doDeleteFile: store.doDeleteFile,
        }).filter(
          (answerSet) =>
            answerSet.jpaRequestAnswerSetID > 0 ||
            answerSet.answers?.length > 0 ||
            answerSet.jpaDynamicTableAnswers?.length > 0 ||
            answerSet.jpaContactAnswers?.length > 0 ||
            answerSet.jpaDynamicTableDeletedAnswers?.length > 0
        );

        if (cleanedAnswerSets.length > 0) {
          data.request.jpaRequestAnswerSets = cleanedAnswerSets;
        } else {
          delete data.request.jpaRequestAnswerSets;
        }
      }
      return apiPut(uri, data, (err, body) => {
        const responseData = body?.data;
        if (
          !err &&
          [ResponseStatuses.Success, ResponseStatuses.SuccessWithWarning, ResponseStatuses.Warning].includes(
            body?.status
          )
        ) {
          const jpaRequestAnswerSets = responseData?.request?.jpaRequestAnswerSets;
          useJpa.setState({ jpaRequestAnswerSets }); // this is needed after updating to add answerIds for questions that have not been answered before so dup records are not created
          setApiAnswers();
          handleSuccess(signed, permitType, body, store, dispatch, SubmissionModal);
          if (hasArChanges) {
            queryClient.invalidateQueries({
              queryKey: [AQUATIC_RESOURCES_QUERY_KEYS.GET_AQUATIC_RESOURCES_VALIDATIONS, requestId],
            });
            void queryClient.invalidateQueries({
              queryKey: [AQUATIC_RESOURCES_QUERY_KEYS.GET_AQUATIC_RESOURCES_USACE_DISTRICT_VALIDATION, requestId],
            });
          }
          queryClient.invalidateQueries({
            queryKey: [DASHBOARD_QUERY_KEYS.GET_DASHBOARD],
          });
          queryClient.invalidateQueries({
            queryKey: [
              PROJECT_QUERY_KEYS.GET_SHARED_CONTACTS,
              store.selectProjectID(),
              store.selectRequestID(),
              store.selectVersion(),
            ],
          });
          queryClient.invalidateQueries({
            queryKey: [PROJECT_QUERY_KEYS.GET_PROJECT_LOCATION],
          });
        } else {
          setIsSavingProgress(false);
          setIsLoading(false);
          openSecondaryModal(statusModal, {
            msg: body?.msg ?? err?.msg ?? 'An error occurred while saving or submitting the request, please try again',
            status: body?.status ?? err?.status ?? 'Request Save/Submission Error',
          });
          dispatch({
            type: 'UPDATE_PERMITS_ERRORS',
            payload: body?.data,
          });
        }
      });
    },
  doUploadAndUpdatePermits:
    (data, status, options = {}) =>
    ({ store }) => {
      const { setIsSavingProgress } = useApp.getState();
      const { openSecondaryModal } = useModal.getState();

      setIsSavingProgress(true);
      if (store.selectValidFiles()?.length > 0) {
        const formdata = new FormData();
        formdata.append('overwrite', false);
        formdata.append('isAnonymous', false);
        store.selectValidFiles().forEach((item) => {
          const fileMetaData = {
            ProjectID: store.selectProjectID(),
            RequestID: store.selectRequestID(),
            Version: 0,
            Type: status,
            Tags: item?.tags,
          };
          formdata.append('files', item?.file);
          formdata.append('fileMetaData', JSON.stringify(fileMetaData));
        });
        Promise.all([store.doUploadFiles(formdata)])
          .then((results) => {
            if (results?.[0]?.status === ResponseStatuses.Success) {
              store.doUpdatePermit(data, options);
              store.doResetUsersFileData();
              store.doFetchUserFilesDetails({
                componentID: store.selectPermitRequestID(),
                scope: StorageScopes.ProjectFolder,
              });
            } else {
              // if any of the files failed to upload
              setIsSavingProgress(false);
              store.doResetUsersFileData();
              openSecondaryModal(statusModal, {
                status: 'ERROR! Failed Uploads',
                msg: (
                  <>
                    <p>{results?.[0]?.msg}</p>
                    <p>Retry saving/submitting your {RequestType.PermitApplication} Request in a few minutes.</p>
                  </>
                ),
                saveText: 'Return to request',
              });
            }
          })
          .catch((e) => {
            console.error(e);
          });
      } else {
        // If no files were selected, just update GP
        store.doUpdatePermit(data, options);
      }
    },
  doFetchGPPermitsByDistrict:
    (district) =>
    ({ dispatch, apiGet }) => {
      const uri =
        '/api/Lookup/getGPPermitsByDistrict?' +
        new URLSearchParams({
          district,
        });

      apiGet(uri, (err, body) => {
        if (!err && body.status === ResponseStatuses.Success) {
          dispatch({
            type: 'UPDATE_GP_PERMIT_TYPES',
            payload: body.data,
          });
        } else {
          console.error('FAILED TO GET LATEST LOOKUP TABLE DATA');
        }
      });
    },
  doUpdatePermitRequest:
    (data) =>
    ({ dispatch, store }) => {
      dispatch({ type: 'UPDATE_PERMITS', payload: data });
      dispatch({ type: 'UPDATE_REQUEST_OBJECT', payload: { permits: [store.selectPermitData()] } });
    },
  doUpdatePermitRequestSub:
    (section, data) =>
    ({ dispatch, store }) => {
      dispatch({ type: 'UPDATE_PERMITS_SUB', payload: { section, data } });
      dispatch({ type: 'UPDATE_REQUEST_OBJECT', payload: { permits: [store.selectPermitData()] } });
    },
  doClearPileDataByUse:
    (pileUse, dataTypesToClear = []) =>
    ({ dispatch, store }) => {
      dispatch({ type: 'CLEAR_PILE_DATA_BY_USE', payload: { pileUse, dataTypesToClear } });
      dispatch({ type: 'UPDATE_REQUEST_OBJECT', payload: { permits: [store.selectPermitData()] } });
    },
  doResetPermitSection:
    (section) =>
    ({ dispatch }) => {
      dispatch({ type: 'RESET_PERMIT_SECTION', payload: section });
    },
  doResetPermitData:
    () =>
    ({ dispatch }) => {
      dispatch({ type: 'RESET_PERMIT' });
    },
};

export default PermitsDataBundle;
